import {
    isEndDateAfterStartDate,
    isNotEmpty,
    isNumber,
} from 'aos-helpers/src/helpers/FormValidation'
import { AosSeverity } from 'aos-services/src/services/common/types/AosSeverity'
import { validateHasAnyOccurrence } from 'aos-services/src/services/tasks/taskRecurrenceScheduleValidator'
import { TaskFormPayload } from 'aos-services/src/services/tasks/types/payload/TaskFormPayload'
import {
    TaskRecurrencePayload,
    TaskRecurrenceValidation,
    validateTaskRecurrence,
} from 'aos-services/src/services/tasks/types/payload/TaskRecurrenceFormPayload'
import { AnimalsTaskType, AnimalType } from 'aos-services/src/services/tasks/types/task/AnimalsTask'
import { BRUSHES_VEHICLE_OTHER_LABEL } from 'aos-services/src/services/tasks/types/task/BrushesVehicle'
import { MaintenanceTaskType } from 'aos-services/src/services/tasks/types/task/MaintenanceTask'
import {
    isTaskTemplateFormPayload,
    TaskTemplateFormPayload,
} from 'aos-services/src/services/tasks/types/task/TaskTemplate'
import { type TaskOrTemplatePayload } from 'aos-services/src/services/tasks/types/task/TaskTemplate'
import { TerminalTaskType } from 'aos-services/src/services/tasks/types/task/TerminalTask'
import { TaskCategory } from 'aos-services/src/services/tasks/types/TaskCategory'
import { TaskStatus } from 'aos-services/src/services/tasks/types/TaskStatus'
import { TaskType } from 'aos-services/src/services/tasks/types/TaskType'
import zod from 'zod'

type IssuePath = (string | number)[]

export { type TaskOrTemplatePayload }

const addIssue = (ctx: zod.RefinementCtx, path: IssuePath) => {
    ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path,
        message: 'invalid',
    })
}

const validateBaseFields = (
    task: TaskFormPayload | TaskTemplateFormPayload,
    ctx: zod.RefinementCtx,
    isTemplate: boolean,
) => {
    if (isTemplate && !isNotEmpty((task as TaskTemplateFormPayload).templateName)) {
        addIssue(ctx, ['templateName'])
    }
    if (!isTemplate && !isNotEmpty(task.title)) {
        addIssue(ctx, ['title'])
    }
    if (!isTemplate && task.billable && !isNotEmpty(task.description)) {
        addIssue(ctx, ['description'])
    }
    if (!isNotEmpty(task.category)) {
        addIssue(ctx, ['category'])
    }
    if (!isNotEmpty(task.severity)) {
        addIssue(ctx, ['severity'])
    }
    if (!isTemplate) {
        const taskWithStatus = task as TaskFormPayload
        if (!isNotEmpty(taskWithStatus.status)) {
            addIssue(ctx, ['status'])
        }
    }
    if (!isNotEmpty(task.processType)) {
        addIssue(ctx, ['processType'])
    }
    if (!isTemplate) {
        const taskWithStatus = task as TaskFormPayload
        if (!isNotEmpty(taskWithStatus.endTime)) {
            addIssue(ctx, ['endTime'])
        }
        if (
            !isEndDateAfterStartDate(taskWithStatus.startTime, taskWithStatus.endTime) ||
            !recurrenceTaskValidation(taskWithStatus.endTime, taskWithStatus.recurrence)
        ) {
            addIssue(ctx, ['endTime'])
        }
        if (
            taskWithStatus.status === TaskStatus.Done &&
            taskWithStatus.severity !== AosSeverity.Maintenance &&
            (!isNumber(taskWithStatus.spendTimeInMinutes) ||
                (taskWithStatus.spendTimeInMinutes as number) <= 0)
        ) {
            addIssue(ctx, ['spendTimeInMinutes'])
        }
    }
    if (!isTemplate && task.category === TaskCategory.Animals && !isNotEmpty(task.location)) {
        addIssue(ctx, ['location'])
    }
    if (!isTemplate && task.contractors?.length) {
        task.contractors.forEach((contractor, index) => {
            if (!isNotEmpty(contractor?.name)) {
                addIssue(ctx, ['contractors', index, 'name'])
            }
            if (!isNotEmpty(contractor?.contractTime)) {
                addIssue(ctx, ['contractors', index, 'contractTime'])
            }
            if (!isNotEmpty(contractor?.jobStart)) {
                addIssue(ctx, ['contractors', index, 'jobStart'])
            }
            if (!isNotEmpty(contractor?.jobEnd)) {
                addIssue(ctx, ['contractors', index, 'jobEnd'])
            }
            if (!isNotEmpty(contractor?.numberOfWorkers)) {
                addIssue(ctx, ['contractors', index, 'numberOfWorkers'])
            }
        })
    }

    if (!isTemplate && (task as TaskFormPayload).recurrence) {
        const taskWithStatus = task as TaskFormPayload
        const scheduleValid = validateHasAnyOccurrence(
            taskWithStatus.recurrence as TaskRecurrencePayload,
            taskWithStatus.startTime,
            taskWithStatus.endTime,
        )
        if (!scheduleValid) {
            {
                addIssue(ctx, ['recurrenceSchedule'])
            }
        }
        const recurrenceValidation = validateTaskRecurrence(
            taskWithStatus.recurrence as TaskRecurrencePayload,
        )
        addRecurrenceIssues(recurrenceValidation, ctx)
    }
}

const addRecurrenceIssues = (validation: TaskRecurrenceValidation, ctx: zod.RefinementCtx) => {
    if ('duration' in validation && !validation.duration) {
        addIssue(ctx, ['recurrence', 'duration'])
    }
    if ('every' in validation && !validation.every) {
        addIssue(ctx, ['recurrence', 'every'])
    }
    if ('weekDays' in validation && !validation.weekDays) {
        addIssue(ctx, ['recurrence', 'weekDays'])
    }
    if ('on' in validation && !validation.on) {
        addIssue(ctx, ['recurrence', 'on'])
    }
    if ('customOnValue' in validation && !validation.customOnValue) {
        addIssue(ctx, ['recurrence', 'customOnValue'])
    }
    if ('month' in validation && !validation.month) {
        addIssue(ctx, ['recurrence', 'month'])
    }
}

const recurrenceTaskValidation = (
    endTime: TaskFormPayload['endTime'],
    recurrence: TaskRecurrencePayload | undefined,
) => (recurrence === undefined || recurrence === null ? true : endTime !== undefined)

export const taskFormSchema = zod.custom<TaskOrTemplatePayload>().superRefine((value, ctx) => {
    if (!value || typeof value !== 'object') {
        {
            addIssue(ctx, ['type'])
        }
        return
    }

    const task = value as TaskOrTemplatePayload
    const template = isTaskTemplateFormPayload(task)
    validateBaseFields(task, ctx, template)

    if (isTaskTemplateFormPayload(task)) {
        return
    }

    switch (task.type) {
        case TaskType.Simple:
            return
        case TaskType.AnimalPrevention: {
            if (!isNotEmpty(task.animalTaskType)) {
                addIssue(ctx, ['animalTaskType'])
            }
            if (!isNotEmpty(task.animalSpecies)) {
                addIssue(ctx, ['animalSpecies'])
            }
            if (!isNotEmpty(task.amount) || (task.amount as number) < 0) {
                addIssue(ctx, ['amount'])
            }
            if (
                task.animalTaskType === AnimalsTaskType.Kill ||
                task.animalTaskType === AnimalsTaskType.Banishment
            ) {
                if (!isNotEmpty(task.cartridges) || (task.cartridges as number) < 0) {
                    addIssue(ctx, ['cartridges'])
                }
            }
            if (task.animalTaskType === AnimalsTaskType.Banishment) {
                if (!isNotEmpty(task.expulsions) || (task.expulsions as number) < 0) {
                    addIssue(ctx, ['expulsions'])
                }
            }
            const isMammalToKill =
                task.animalSpecies?.animalType === AnimalType.Mammal &&
                task.animalSpecies.horns &&
                task.animalTaskType === AnimalsTaskType.Kill
            if (isMammalToKill) {
                if (!isNotEmpty(task.lifeStage)) {
                    addIssue(ctx, ['lifeStage'])
                }
                if (!isNotEmpty(task.gender)) {
                    addIssue(ctx, ['gender'])
                }
            }
            if (task.horns) {
                if (
                    !isNotEmpty(task.amountOfSpikes) ||
                    (task.amountOfSpikes as number) <= 0 ||
                    (task.amountOfSpikes as number) > 50
                ) {
                    {
                        addIssue(ctx, ['amountOfSpikes'])
                    }
                }
            }
            return
        }
        case TaskType.DeIcing: {
            if (!isNotEmpty(task.area)) {
                addIssue(ctx, ['area'])
            }
            if (!isNotEmpty(task.surfaceType)) {
                addIssue(ctx, ['surfaceType'])
            }
            if (!isNumber(task.temperature)) {
                addIssue(ctx, ['temperature'])
            }
            task.chemicalsUsage?.forEach((usage, index) => {
                if (!isNumber(usage.amount) || (usage.amount as number) > 200) {
                    addIssue(ctx, ['chemicalsUsage', index, 'amount'])
                }
                if (!isNotEmpty(usage.chemicals)) {
                    addIssue(ctx, ['chemicalsUsage', index, 'chemicals'])
                }
                if (!isNumber(usage.totalAmount)) {
                    addIssue(ctx, ['chemicalsUsage', index, 'totalAmount'])
                }
            })
            return
        }
        case TaskType.Maintenance: {
            if (!task.assignments?.length) {
                {
                    addIssue(ctx, ['assignments'])
                }
                return
            }
            task.assignments.forEach((assignment, index) => {
                if (!isNotEmpty(assignment?.job)) {
                    addIssue(ctx, ['assignments', index, 'job'])
                }
                if (assignment?.job?.jobType === MaintenanceTaskType.Brushes) {
                    if (!isNotEmpty(assignment?.brushesVehicle)) {
                        addIssue(ctx, ['assignments', index, 'brushesVehicle'])
                    }
                    if (
                        assignment?.brushesVehicle?.label === BRUSHES_VEHICLE_OTHER_LABEL &&
                        !isNotEmpty(assignment?.newBrushVehicle)
                    ) {
                        addIssue(ctx, ['assignments', index, 'newBrushVehicle'])
                    }
                    if (!isNotEmpty(assignment?.brushesBrand)) {
                        addIssue(ctx, ['assignments', index, 'brushesBrand'])
                    }
                    if (!isNotEmpty(assignment?.engineLastShiftHours)) {
                        addIssue(ctx, ['assignments', index, 'engineLastShiftHours'])
                    }
                    if (!isNotEmpty(assignment?.brushesLastShiftHours)) {
                        addIssue(ctx, ['assignments', index, 'brushesLastShiftHours'])
                    }
                    if (!isNotEmpty(assignment?.totalHours)) {
                        addIssue(ctx, ['assignments', index, 'totalHours'])
                    }
                }
            })
            return
        }
        case TaskType.Inspection: {
            if (!task.items || task.items.length === 0) {
                {
                    addIssue(ctx, ['items'])
                }
            }
            return
        }
        case TaskType.RescueService: {
            if (!isNotEmpty(task.taskType)) {
                addIssue(ctx, ['taskType'])
            }
            task.units?.forEach((unit, index) => {
                if (!isNotEmpty(unit?.name)) {
                    addIssue(ctx, ['units', index, 'name'])
                }
                if (!isNotEmpty(unit?.numberOfWorkers)) {
                    addIssue(ctx, ['units', index, 'numberOfWorkers'])
                }
            })
            return
        }
        case TaskType.Refuelling: {
            if (!isNotEmpty(task.refuellingTaskType)) {
                addIssue(ctx, ['refuellingTaskType'])
            }
            return
        }
        case TaskType.Terminal: {
            if (!isNotEmpty(task.terminalTaskType)) {
                addIssue(ctx, ['terminalTaskType'])
            }
            if (task.terminalTaskType === TerminalTaskType.HeatingWires) {
                if (!isNotEmpty(task.licensePlate)) {
                    addIssue(ctx, ['licensePlate'])
                }
            }
            return
        }
        default:
            return
    }
})
